/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-unused-vars */
import { env, envBoolean, envFloat, envJson } from './env'

declare var process: any

type TT =
  | StringConstructor
  | NumberConstructor
  | BooleanConstructor
  | ObjectConstructor
  | ArrayConstructor

class EnvConfig<T extends TT> {
  constructor(
    public readonly type: T,
    public readonly defaultValue?: ReturnType<T> | null
  ) {}
}

export const e = <T extends TT>(type: T, defaultValue?: ReturnType<T> | null) =>
  new EnvConfig(type, defaultValue)

type EnvObjType<T> = T extends object ? { [K in keyof T]: EnvType<T[K]> } : T
type EnvType<T> = T extends EnvConfig<infer X> ? ReturnType<X> : EnvObjType<T>

function resolvePath(...keys: string[]): string {
  return keys.reduce((acc: string, key: string) => {
    if (key) {
      if (acc) return `${acc}_${key}`
      else return key
    }
    return acc
  }, '')
}

function readEnv<T>(envName: string, type: T, namespace = '') {
  envName = namespace ? `${namespace}_${envName}` : envName
  switch (type) {
    case Number:
      return envFloat(envName)
    case Boolean:
      return envBoolean(envName)
    case Object:
    case Array:
      return envJson(envName)
    default:
      return env(envName)
  }
}

export function readConfigInternal<T>(
  obj: T,
  parent = '',
  namespace: string,
  missing: string[] = []
): EnvType<T> {
  if (obj instanceof EnvConfig) {
    const value = readEnv(parent, obj.type, namespace)
    if (value === undefined) {
      if (obj.defaultValue === undefined) {
        if (namespace) {
          missing.push(`${namespace}_${parent}`)
        } else {
          missing.push(`${parent}`)
        }
      }
      return obj.defaultValue as EnvType<T>
    }
    return value as EnvType<T>
  } else if (typeof obj === 'object' && obj) {
    type Keys = keyof typeof obj
    const keys = Object.keys(obj) as Keys[]
    return keys
      .map(key => {
        const child = obj[key] as T[keyof T]
        const value = readConfigInternal(
          child,
          resolvePath(parent, String(key)),
          namespace,
          missing
        )
        return { key, value }
      })
      .reduce(
        (acc, { key, value }) => {
          acc[key] = value
          return acc
        },
        {} as Record<Keys, EnvType<T[keyof T]>>
      ) as EnvType<T>
  } else {
    return obj as EnvType<T>
  }
}

export function readConfig<T>(obj: T, namespace = '') {
  const missing: string[] = []
  const root = readConfigInternal(obj, '', namespace, missing)
  if (missing.length > 0) {
    console.error('Missing required environment variables', missing)
    if (typeof process !== 'undefined' && process.exit) process.exit(1)
  }
  return root
}
